import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Drawer, Select, InputNumber, Button, Tag, Spin, Empty, Tooltip, Popconfirm,
  Modal, Input, message as antMessage, Checkbox, Tabs, Alert, Avatar, Collapse,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined,
  CheckCircleFilled, ShareAltOutlined, SendOutlined, CarOutlined, CalculatorOutlined,
  ShopOutlined, UserOutlined, QuestionCircleOutlined, EyeOutlined, UndoOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useSocket } from "../../../contexts/SocketContext";
import { substituteQRText, loadConditionLabels, conditionLabel, loadSideLabels, sideLabel, useQv4Theme } from "../../helpers";
import { UnavailablePartsModal } from "../modals-misc/UnavailablePartsModal";
import { fetchSuppliersV2 } from "../../models/suppliers/suppliersModel";
import { fetchCostPrices, getCostPriceTier } from "../../models/shared/costPricesModel";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";
import { fetchQuickReplies } from "../../models/chat/chatUtilityModel";
import {
  addSupplierQuote, editSupplierQuote, disableSupplierQuote, setPartQuote,
  fetchApprovalHistory, withdrawApproval, shareQuote, fetchQuoteSharePreflight,
} from "../../models/price-drawer/pricingModel";
import { usePartHistoryQuotes } from "../../viewmodels/shared/usePartHistoryQuotes";
import { PartHistoryList } from "../shared/PartHistoryList";

dayjs.extend(utc);

// ── Pricing helpers ───────────────────────────────────────────────────────────

// GP% = (SP - CP) / CP × 100  →  SP = CP × (1 + GP/100)
export function spFromGp(cp, gp) {
  if (cp <= 0) return cp;
  return Math.round(cp * (1 + gp / 100) * 100) / 100;
}
export function gpFromSp(cp, sp) {
  if (cp <= 0) return 0;
  return Math.round(((sp - cp) / cp) * 10000) / 100; // 2 dp
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = { supplier_id: null, price: null, warranty_days: null, delivery_charge: null, gp: null, sp: null };

// Cost → recommended GP%/SP pair from the tier table (null gp/sp when cost is empty).
function recommendedPricing(costPrices, cost) {
  if (cost == null) return { gp: null, sp: null };
  const tier = getCostPriceTier(costPrices, cost);
  const gp = tier ? Number(tier.GP_Percentage) : 0;
  return { gp, sp: spFromGp(cost, gp) };
}
const TD_STYLE = { padding: "8px 4px", verticalAlign: "middle" };
const TH_STYLE = { fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: "0.04em", padding: "6px 4px" };
// B2C / B2B quick reply IDs — update here if templates change
export const QR_IDS = { b2c: "6a37b64f495446fce3b7eaed", b2b: "6a37b687dc39cd11d9538bf1" };

// ── Main component ────────────────────────────────────────────────────────────

export function PriceUpdateDrawer({ card, open, onClose, userId, onPartsLoaded, authState, approvalDecidedTick, userMap }) {
  const { socket: mainSocket } = useSocket();
  const { t, isDark } = useQv4Theme();

  const [parts, setParts] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [costPrices, setCostPrices] = useState([]);
  const [costPricesLoading, setCostPricesLoading] = useState(false);
  const taskLoadRequestRef = useRef(0);

  // quotePricing: { [quoteId]: { sp, gp, _cp } }
  // _cp is stored to detect when quoted_price was changed on edit
  const [quotePricing, setQuotePricing] = useState({});

  const [addingPartId, setAddingPartId] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);

  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const [selectingPartId, setSelectingPartId] = useState(null);
  const [taskCustomer, setTaskCustomer] = useState(null);
  const [taskCustomerType, setTaskCustomerType] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [unavailableNoticeParts, setUnavailableNoticeParts] = useState(null);
  const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
  const [resubmitReason, setResubmitReason] = useState("");
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [withdrawing, setWithdrawing] = useState(false);

  const pendingApproval = approvalHistory.find(a => a.status === "pending") || null;
  const [previewOpen, setPreviewOpen] = useState(false);

  // This drawer is reachable from any status (WorkflowTaskDrawer's Parts tab
  // opens it universally). draft/waiting_for_price are the only "normal"
  // editing stages — anything past that (sent for confirmation, payment in
  // flight/done, cancelled, completed, parked) is an override worth flagging.
  const isOverrideStatus = card?.status && card.status !== "draft" && card.status !== "waiting_for_price";
  const [overrideConfirm, setOverrideConfirm] = useState(null); // { title, description, onConfirm } | null
  const [overrideNote, setOverrideNote] = useState("");

  // Wraps `action` so that outside the normal draft/waiting_for_price editing
  // stages, staff see a confirm+optional-note dialog before an override
  // (add/edit/delete/select quote, share quote) proceeds. `action` receives
  // the trimmed note (or null).
  const withOverrideGuard = (title, description, action) => {
    if (!isOverrideStatus) { action(null); return; }
    setOverrideNote("");
    setOverrideConfirm({ title, description, onConfirm: action });
  };

  // Keep parent badge accurate
  useEffect(() => {
    if (!open || !card?.id || !parts.length) return;
    onPartsLoaded?.(card.id, parts);
  }, [parts, card?.id, open]); // eslint-disable-line

  // Fetch parts + approval history on open
  useEffect(() => {
    const requestId = ++taskLoadRequestRef.current;
    if (!open || !card?.id) { setParts([]); setQuotePricing({}); setApprovalHistory([]); setTaskCustomer(null); setTaskCustomerType(null); return undefined; }
    setTaskLoading(true);
    setParts([]);
    setQuotePricing({});
    setApprovalHistory([]);
    setTaskCustomer(null);
    setTaskCustomerType(null);
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
    fetchTaskDetail(card.id)
      .then(taskData => {
        if (requestId !== taskLoadRequestRef.current) return;
        if (!taskData) return;
        const taskObj = taskData.task ?? taskData;
        if (taskObj.customer) setTaskCustomer(taskObj.customer);
        const ct = taskObj.customer_type ?? taskObj.customer?.type ?? taskObj.customer?.customer_type ?? null;
        if (ct != null) setTaskCustomerType(ct);
        const rawParts = taskData.parts ?? taskObj.parts ?? [];
        setParts(rawParts.map(p => ({
          ...p,
          supplier_quotes: p.supplier_quotes ?? p.quotes ?? [],
        })));
      })
      .catch(error => {
        if (requestId === taskLoadRequestRef.current) {
          antMessage.error(error?.response?.data?.message || error.message || "Could not load query pricing");
        }
      })
      .finally(() => {
        if (requestId === taskLoadRequestRef.current) setTaskLoading(false);
      });
    fetchApprovalHistory(card.id)
      .then(list => {
        if (requestId === taskLoadRequestRef.current && list) setApprovalHistory(list);
      })
      .catch(() => {});
    return () => {
      if (requestId === taskLoadRequestRef.current) taskLoadRequestRef.current += 1;
    };
  }, [open, card?.id]); // eslint-disable-line

  // Fetch cost prices once (cached)
  useEffect(() => {
    setCostPricesLoading(true);
    fetchCostPrices().then(data => {
      setCostPrices(data);
      setCostPricesLoading(false);
    });
  }, []);

  // On load: restore GP% for already-selected quotes that have a saved selling_price
  // (that value is authoritative — it's what's actually persisted). Every other
  // quote — unselected, or not yet priced — gets a tier-lookup preview so every
  // row shows a sensible number immediately without waiting on "Calculate prices".
  // These previews are NOT persisted anywhere until their quote is selected.
  useEffect(() => {
    if (!parts.length) return;
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (next[q.id]) return; // don't clobber an existing (possibly edited) entry
          const cp = Number(q.quoted_price || 0);
          if (p.selected_quote_id === q.id && p.selling_price != null) {
            const sp = Number(p.selling_price);
            next[q.id] = { sp, gp: gpFromSp(cp, sp), _cp: cp };
          } else {
            const rec = recommendedPricing(costPrices, cp);
            next[q.id] = { sp: rec.sp, gp: rec.gp, _cp: cp };
          }
        });
      });
      return next;
    });
  }, [parts, costPrices]); // eslint-disable-line

  // Real-time socket updates from other users
  useEffect(() => {
    if (!mainSocket || !open || !card?.id) return;

    const handleQuotesUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { quote, event } = data.data;
      if (!quote) return;
      setParts(prev => prev.map(p => {
        if (p.id !== quote.query_part_id) return p;
        const qs = p.supplier_quotes ?? [];
        if (event === "quote_disabled") {
          return { ...p, supplier_quotes: qs.filter(q => q.id !== quote.id) };
        }
        const exists = qs.some(q => q.id === quote.id);
        return {
          ...p,
          supplier_quotes: exists
            ? qs.map(q => q.id === quote.id ? { ...q, ...quote } : q)
            : [...qs, quote],
        };
      }));
    };

    const handlePartsUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { part, event } = data.data;
      if (!part) return;
      if (event === "part_disabled") {
        setParts(prev => prev.filter(p => p.id !== part.id));
      } else {
        setParts(prev => prev.map(p => p.id === part.id ? { ...p, ...part } : p));
      }
    };

    mainSocket.on("query:quotes_updated", handleQuotesUpdated);
    mainSocket.on("query:parts_updated", handlePartsUpdated);
    return () => {
      mainSocket.off("query:quotes_updated", handleQuotesUpdated);
      mainSocket.off("query:parts_updated", handlePartsUpdated);
    };
  }, [mainSocket, open, card?.id]); // eslint-disable-line

  // Supplier list (cached)
  useEffect(() => {
    if (allSuppliers.length) return;
    fetchSuppliersV2().then(s => setAllSuppliers(s));
  }, []); // eslint-disable-line

  const supplierOptions = useMemo(() =>
    allSuppliers.map(s => ({
      value: s.id,
      label: s.name && s.name !== s.number ? s.name : s.number,
      name: s.name || "",
      number: s.number || "",
    })),
    [allSuppliers]
  );

  // Order-level delivery: based on total cost of all selected quotes
  const orderDelivery = useMemo(() => {
    if (!costPrices.length) return null;
    const total = parts.reduce((sum, p) => {
      if (!p.selected_quote_id) return sum;
      const q = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
      return sum + Number(q?.quoted_price || 0);
    }, 0);
    if (total === 0) return null;
    const tier = getCostPriceTier(costPrices, total);
    return tier ? Number(tier.DeliveryCost) : null;
  }, [parts, costPrices]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleAddQuote = async (partId, note) => {
    if (!addForm.supplier_id || addForm.price == null) return;
    setAddSaving(true);
    try {
      const res = await addSupplierQuote(partId, {
        supplierId: addForm.supplier_id,
        price: addForm.price,
        warrantyDays: addForm.warranty_days ?? 0,
        deliveryCharge: addForm.delivery_charge ?? 0,
        userId,
        ...(note ? { remarks: note } : {}),
      });
      if (res.success) {
        const newQuote = res.data.quote;
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: [...(p.supplier_quotes ?? []), newQuote] }
            : p
        ));
        // The form already computed/holds the GP%/SP pair (auto-calculated from
        // cost, or manually adjusted) — carry it over as-is rather than
        // recomputing, so a manual tweak made just before saving isn't lost.
        const cp = Number(newQuote.quoted_price || addForm.price || 0);
        const gp = addForm.gp ?? 0;
        const sp = addForm.sp ?? cp;
        setQuotePricing(prev => ({ ...prev, [newQuote.id]: { sp, gp, _cp: cp } }));
        setAddingPartId(null);
        setAddForm(EMPTY_FORM);
      }
    } catch {}
    setAddSaving(false);
  };

  const handleEditQuote = async (quoteId, partId, note) => {
    if (editForm.price == null) return;
    setEditSaving(true);
    try {
      const res = await editSupplierQuote(quoteId, {
        supplierId: editForm.supplier_id,
        price: editForm.price,
        deliveryCharge: editForm.delivery_charge ?? 0,
        warrantyDays: editForm.warranty_days ?? 0,
        userId,
        ...(note ? { remarks: note } : {}),
      });
      if (res.success) {
        const updated = res.data;
        setParts(prev => prev.map(p => ({
          ...p,
          supplier_quotes: (p.supplier_quotes ?? []).map(q =>
            q.id === quoteId ? { ...q, ...updated } : q
          ),
        })));
        // Same rule as add: trust the form's own GP%/SP rather than recomputing.
        const newCp = Number(updated.quoted_price || editForm.price || 0);
        const gp = editForm.gp ?? 0;
        const sp = editForm.sp ?? newCp;
        setQuotePricing(prev => ({ ...prev, [quoteId]: { sp, gp, _cp: newCp } }));
        // If this quote is already the part's selected quote, its selling price
        // drives what the customer sees — persist it now, otherwise editing an
        // already-selected quote's price silently never reaches query_parts.
        const part = parts.find(p => p.id === partId);
        if (part?.selected_quote_id === quoteId) patchSellingPrice(partId, quoteId, sp);
        setEditingQuoteId(null);
        setEditForm(EMPTY_FORM);
      }
    } catch {}
    setEditSaving(false);
  };

  const onDeleteQuote_original = async (quoteId, partId) => {
    try {
      const res = await disableSupplierQuote(quoteId, userId);
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: (p.supplier_quotes ?? []).filter(q => q.id !== quoteId) }
            : p
        ));
      }
    } catch {}
  };

  const handleSelectQuote = async (partId, quoteId) => {
    setSelectingPartId(partId);
    try {
      const part = parts.find(p => p.id === partId);
      const newQuoteId = part?.selected_quote_id === quoteId ? null : quoteId;
      const sellingPrice = newQuoteId ? (quotePricing[newQuoteId]?.sp ?? null) : null;
      const res = await setPartQuote(partId, { selectedQuoteId: newQuoteId, sellingPrice, userId });
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, selected_quote_id: newQuoteId, selling_price: sellingPrice }
            : p
        ));
      }
    } catch {}
    setSelectingPartId(null);
  };

  // Count quotes not yet priced (to drive the button label)
  const unpricedCount = useMemo(() =>
    parts.reduce((n, p) =>
      n + (p.supplier_quotes ?? []).filter(q => !quotePricing[q.id]).length, 0),
    [parts, quotePricing]
  );

  // Supplier price history — one request for every part in this task, fed
  // down to each PartSection as its own slice. card.vehicle is shaped
  // inconsistently depending on how this drawer was opened: WorkflowTaskDrawer
  // names the raw make/model FK ids `brand`/`model`, the board's own
  // priceCard path names the same data `brand_id`/`model_id` — read both.
  // See docs/superpowers/specs/2026-08-22-supplier-price-history-design.md.
  const { resultsByKey: partHistoryByKey } = usePartHistoryQuotes({
    makeId: card?.vehicle?.brand_id ?? card?.vehicle?.brand ?? null,
    modelId: card?.vehicle?.model_id ?? card?.vehicle?.model ?? null,
    year: card?.vehicle?.year ?? null,
    countrySpec: card?.vehicle?.country_spec ?? null,
    excludeQueryPk: card?.id ?? null,
    parts: parts.map(p => ({ key: String(p.id), partId: p.part_id ?? null, partName: p.part_name })),
  });

  const patchSellingPrice = (partId, quoteId, sp) => {
    const part = parts.find(p => p.id === partId);
    setPartQuote(partId, { selectedQuoteId: quoteId, sellingPrice: sp, userId }).then(res => {
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId ? { ...p, selling_price: sp } : p
        ));
        if (part?.quoted_final_price != null && Number(part.quoted_sp_snapshot) !== Number(sp)) {
          antMessage.warning("This part was already quoted to the customer — the invoice will keep using the last-quoted price until you re-share this part's quote.");
        }
      }
    }).catch(() => {});
  };

  const handleCalculateAll = () => {
    if (!costPrices.length) return;
    const toSave = []; // [{ partId, quoteId, sp }] for already-selected quotes
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (next[q.id]) return; // skip already-priced (preserve manual edits)
          const cp = Number(q.quoted_price || 0);
          const tier = getCostPriceTier(costPrices, cp);
          const gp = tier ? Number(tier.GP_Percentage) : 0;
          const sp = spFromGp(cp, gp);
          next[q.id] = { sp, gp, _cp: cp };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };

  const handleRoundAll = () => {
    const toSave = [];
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (!next[q.id]) return; // only round already-priced quotes
          const rounded = Math.round(next[q.id].sp);
          const cp = next[q.id]._cp ?? Number(q.quoted_price || 0);
          next[q.id] = { ...next[q.id], sp: rounded, gp: gpFromSp(cp, rounded) };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp: rounded });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };

  const refreshApprovalHistory = () => {
    if (!card?.id) return;
    fetchApprovalHistory(card.id).then(list => { if (list) setApprovalHistory(list); });
  };

  // Refresh history when admin approves or rejects (socket tick from QuerysetV4)
  useEffect(() => {
    if (approvalDecidedTick > 0 && open) refreshApprovalHistory();
  }, [approvalDecidedTick]); // eslint-disable-line

  const handleWithdrawApproval = async () => {
    if (!pendingApproval) return;
    setWithdrawing(true);
    try {
      const res = await withdrawApproval(pendingApproval.id, userId);
      if (!res.success) throw new Error(res.message);
      setApprovalHistory(prev => prev.filter(a => a.id !== pendingApproval.id));
      antMessage.success("Approval request withdrawn");
    } catch (e) {
      antMessage.error(e?.response?.data?.message || e?.message || "Failed to withdraw approval request");
    } finally {
      setWithdrawing(false);
    }
  };

  const handleClose = () => {
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
    onClose();
  };

  const v = card?.vehicle;
  const vehicleLabel = v
    ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
    : null;
  const customerPhone = card?.customer?.customer_mobile || card?.customer_mobile || null;

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              Price Update{card?.query_id ? ` — #${card.query_id}` : ""}
            </div>
            {vehicleLabel && (
              <div style={{ fontSize: 12, color: t.textSub, fontWeight: 400, marginTop: 2 }}>{vehicleLabel}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {unpricedCount > 0 && (
              <Tooltip title={`Fill GP% and selling price for ${unpricedCount} unpriced quote${unpricedCount !== 1 ? "s" : ""} using cost tier lookup`}>
                <Button
                  icon={<CalculatorOutlined />}
                  size="small"
                  loading={costPricesLoading}
                  disabled={costPricesLoading}
                  onClick={handleCalculateAll}
                  style={{ flexShrink: 0 }}
                >
                  Calculate prices ({unpricedCount})
                </Button>
              </Tooltip>
            )}
            {Object.keys(quotePricing).length > 0 && (
              <Tooltip title="Round all selling prices to nearest whole number">
                <Button
                  icon={<span style={{ fontWeight: 700, fontSize: 11 }}>.0</span>}
                  size="small"
                  onClick={handleRoundAll}
                  style={{ flexShrink: 0 }}
                >
                  Round prices
                </Button>
              </Tooltip>
            )}
            {customerPhone && (
              <Tooltip title={pendingApproval ? "A quote is already awaiting admin approval — withdraw it first to submit a new one" : "Share quote to customer"}>
                <Button
                  icon={<ShareAltOutlined />}
                  size="small"
                  disabled={!!pendingApproval}
                  onClick={() => withOverrideGuard(
                    "Share quote after payment?",
                    "This query already has a payment in progress or completed. Sharing a new quote will submit it through the normal customer-approval flow again.",
                    () => {
                      const hasApproved = approvalHistory.some(a => a.status === "approved");
                      if (hasApproved) { setResubmitReason(""); setResubmitModalOpen(true); }
                      else setShareOpen(true);
                    }
                  )}
                  style={{ flexShrink: 0 }}
                >
                  Share quote
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      }
      open={open}
      onClose={handleClose}
      width={780}
      destroyOnHidden
      styles={{ body: { padding: 16, overflowY: "auto" } }}
    >
      {approvalHistory.length > 0 && (() => {
        const latest = approvalHistory[0];
        const isPending = latest.status === "pending";
        const isRejected = latest.status === "rejected";
        const deciderName = latest.decided_by ? (userMap?.[latest.decided_by] ?? `User #${latest.decided_by}`) : null;
        const older = approvalHistory.slice(1);
        return (
          <div style={{ marginBottom: 12 }}>
            <Alert
              type={isPending ? "warning" : isRejected ? "error" : "success"}
              showIcon
              message={
                isPending ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ fontSize: 12 }}>
                      Quote pending admin approval since{" "}
                      <strong>{dayjs.utc(latest.submitted_at.replace(" ", "T")).format("DD/MM/YYYY HH:mm")}</strong>
                      {" "}— you cannot submit another until this is decided or withdrawn.
                      {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                        <div style={{ marginTop: 4, color: "#d48806" }}>
                          Resubmit reason: {latest.resubmission_reason}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>View quote</Button>
                      <Popconfirm
                        title="Withdraw approval request?"
                        description="This cannot be undone. You can re-submit after withdrawing."
                        onConfirm={handleWithdrawApproval}
                        okText="Withdraw"
                        okButtonProps={{ danger: true }}
                        cancelText="Keep"
                      >
                        <Button size="small" danger loading={withdrawing}>Withdraw</Button>
                      </Popconfirm>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    <strong>{isRejected ? "Quote rejected" : "Quote approved"}</strong>
                    {deciderName && <> by <strong>{deciderName}</strong></>}
                    {isRejected && latest.decision_reason && <> — {latest.decision_reason}</>}
                    {isRejected && <> · You can now update and re-submit for approval.</>}
                    {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                      <div style={{ marginTop: 4, color: "#d48806" }}>
                        Resubmit reason: {latest.resubmission_reason}
                      </div>
                    )}
                  </div>
                )
              }
            />
            {older.length > 0 && (
              <Collapse
                ghost
                size="small"
                style={{ marginTop: 2 }}
                items={[{
                  key: "hist",
                  label: <span style={{ fontSize: 11, color: "#8c8c8c" }}>Previous approvals ({older.length})</span>,
                  children: (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {older.map(a => {
                        const dName = a.decided_by ? (userMap?.[a.decided_by] ?? `User #${a.decided_by}`) : null;
                        const aRejected = a.status === "rejected";
                        const aApproved = a.status === "approved";
                        return (
                          <div key={a.id} style={{
                            fontSize: 11, padding: "6px 10px", borderRadius: 4,
                            background: aRejected
                              ? (isDark ? "rgba(255,77,79,0.12)" : "#fff2f0")
                              : aApproved
                                ? (isDark ? "rgba(69,153,34,0.12)" : "#f6ffed")
                                : (isDark ? "rgba(250,173,20,0.12)" : "#fffbe6"),
                            border: `1px solid ${aRejected ? "#ffccc7" : aApproved ? "#b7eb8f" : "#ffe58f"}`,
                          }}>
                            <span style={{ fontWeight: 600, color: aRejected ? "#cf1322" : aApproved ? "#389e0d" : "#d48806" }}>
                              {aRejected ? "Rejected" : aApproved ? "Approved" : "Pending"}
                            </span>
                            {dName && <span style={{ color: "#595959" }}> by {dName}</span>}
                            {a.decided_at && (
                              <span style={{ color: "#8c8c8c" }}>
                                {" "}· {dayjs.utc(a.decided_at.replace(" ", "T")).format("DD MMM · HH:mm")}
                              </span>
                            )}
                            {a.resubmission_reason && a.resubmission_reason !== "null" && (
                              <div style={{ color: "#d48806", marginTop: 2 }}>Resubmit reason: {a.resubmission_reason}</div>
                            )}
                            {a.decision_reason && (
                              <div style={{ color: "#8c8c8c", marginTop: 2 }}>Decision: {a.decision_reason}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ),
                }]}
              />
            )}
          </div>
        );
      })()}
      {taskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div>
      ) : parts.length === 0 ? (
        <Empty description="No parts found" style={{ marginTop: 48 }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {parts.map(part => (
            <PartSection
              key={part.id}
              part={part}
              supplierOptions={supplierOptions}
              quotePricing={quotePricing}
              costPrices={costPrices}
              addingPartId={addingPartId}
              setAddingPartId={setAddingPartId}
              addForm={addForm}
              setAddForm={setAddForm}
              addSaving={addSaving}
              onAddQuote={(partId) => withOverrideGuard(
                "Add quote after payment?",
                "This query already has a payment in progress or completed. Adding a new quote here is a record-keeping override.",
                (note) => handleAddQuote(partId, note)
              )}
              editingQuoteId={editingQuoteId}
              setEditingQuoteId={setEditingQuoteId}
              editForm={editForm}
              setEditForm={setEditForm}
              editSaving={editSaving}
              onEditSave={(quoteId, partId) => withOverrideGuard(
                "Edit quote after payment?",
                "This query already has a payment in progress or completed. Editing this quote is a record-keeping override.",
                (note) => handleEditQuote(quoteId, partId, note)
              )}
              onDeleteQuote={(quoteId, partId) => withOverrideGuard(
                "Delete quote after payment?",
                "This query already has a payment in progress or completed.",
                () => onDeleteQuote_original(quoteId, partId)
              )}
              onSelectQuote={(partId, quoteId) => withOverrideGuard(
                "Change selected quote after payment?",
                "This query already has a payment in progress or completed. This changes which quote is used for pricing.",
                () => handleSelectQuote(partId, quoteId)
              )}
              selectingPartId={selectingPartId}
              history={partHistoryByKey[String(part.id)] || []}
              onUseHistoryPrice={(match) => {
                setAddingPartId(part.id);
                setAddForm({
                  supplier_id: supplierOptions.some(s => s.value === match.supplier_id) ? match.supplier_id : null,
                  price: match.quoted_price,
                  warranty_days: match.warranty_days ?? 0,
                  delivery_charge: 0,
                  gp: null,
                  sp: null,
                });
              }}
            />
          ))}

          {/* Order-level delivery charge */}
          {orderDelivery != null && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", borderRadius: 8,
              background: isDark ? "rgba(69,153,34,0.12)" : "#f6ffed", border: "1px solid #b7eb8f",
              fontSize: 13,
            }}>
              <CarOutlined style={{ color: "#52c41a" }} />
              <span style={{ color: t.textSub }}>Order delivery charge:</span>
              <span style={{ fontWeight: 700, color: "#389e0d" }}>AED {orderDelivery.toLocaleString()}</span>
              <span style={{ fontSize: 11, color: t.textSub, marginLeft: 4 }}>(based on total selected cost)</span>
            </div>
          )}
        </div>
      )}

      <Modal
        open={resubmitModalOpen}
        title="Reason for new quote"
        okText="Continue"
        okButtonProps={{ disabled: !resubmitReason.trim() }}
        onOk={() => { setResubmitModalOpen(false); setShareOpen(true); }}
        onCancel={() => setResubmitModalOpen(false)}
        width={420}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          A quote for this query was already approved. Please explain why you are submitting a new one — the admin will see this reason.
        </p>
        <Input.TextArea
          autoFocus
          rows={3}
          maxLength={500}
          showCount
          placeholder="e.g. Wrong part was quoted, customer requested a different option..."
          value={resubmitReason}
          onChange={e => setResubmitReason(e.target.value)}
        />
      </Modal>

      <ShareQuoteModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        card={card}
        taskCustomer={taskCustomer}
        taskCustomerType={taskCustomerType}
        parts={parts}
        quotePricing={quotePricing}
        orderDelivery={orderDelivery}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        resubmissionReason={resubmitReason || null}
        onApprovalCreated={refreshApprovalHistory}
        onSuggestUnavailableNotice={setUnavailableNoticeParts}
      />

      <ShareQuoteModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        card={card}
        parts={parts}
        quotePricing={quotePricing}
        orderDelivery={orderDelivery}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        readOnly
        preloadedPayload={pendingApproval?.payload}
      />

      <UnavailablePartsModal
        open={!!unavailableNoticeParts}
        queryPk={card?.id}
        queryId={card?.query_id}
        customerName={taskCustomer?.customer_name || card?.customer?.customer_name}
        customerPhone={customerPhone}
        unavailableParts={unavailableNoticeParts || []}
        userId={userId}
        onClose={() => setUnavailableNoticeParts(null)}
        onSuccess={() => setUnavailableNoticeParts(null)}
      />

      <Modal
        open={!!overrideConfirm}
        title={overrideConfirm?.title}
        okText="Continue"
        okButtonProps={{ danger: true }}
        onOk={() => { const c = overrideConfirm; setOverrideConfirm(null); c?.onConfirm(overrideNote.trim() || null); }}
        onCancel={() => setOverrideConfirm(null)}
        width={440}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>{overrideConfirm?.description}</p>
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Optional note (e.g. price matched competitor)"
          value={overrideNote}
          onChange={e => setOverrideNote(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
}

// ── Part section ──────────────────────────────────────────────────────────────

function PartSection({
  part, supplierOptions, quotePricing, costPrices,
  addingPartId, setAddingPartId, addForm, setAddForm, addSaving, onAddQuote,
  editingQuoteId, setEditingQuoteId, editForm, setEditForm, editSaving,
  onEditSave, onDeleteQuote, onSelectQuote, selectingPartId,
  history = [], onUseHistoryPrice,
}) {
  const quotes = part.supplier_quotes ?? [];
  const isAdding = addingPartId === part.id;
  const isSelecting = selectingPartId === part.id;
  const { t } = useQv4Theme();

  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, overflow: "hidden" }}>
      {/* Part header */}
      <div style={{
        background: t.headerBg, padding: "9px 14px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{part.part_name}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {part.selected_quote_id && (
            <span style={{ fontSize: 11, color: "#389e0d", fontWeight: 600 }}>
              <CheckCircleFilled style={{ marginRight: 4 }} />
              Quote selected
              {part.selling_price != null && (
                <span style={{ marginLeft: 6, fontWeight: 700 }}>
                  · AED {Number(part.selling_price).toLocaleString()}
                </span>
              )}
            </span>
          )}
          <Tag color={quotes.length > 0 ? "blue" : "default"} style={{ margin: 0 }}>
            {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
          </Tag>
        </div>
      </div>

      {/* Quotes table */}
      {(quotes.length > 0 || isAdding) && (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 64 }} />
          </colgroup>
          <thead>
            <tr style={{ background: t.headerBg, borderBottom: `1px solid ${t.border}` }}>
              <th style={{ ...TH_STYLE, paddingLeft: 14 }} />
              <th style={{ ...TH_STYLE, textAlign: "left" }}>SUPPLIER</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>COST</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>WARRANTY</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>DELIVERY</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>GP %</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>SELL PRICE</th>
              <th style={{ ...TH_STYLE, paddingRight: 14 }} />
            </tr>
          </thead>
          <tbody>
            {quotes.map(q =>
              editingQuoteId === q.id ? (
                <QuoteFormRow
                  key={q.id}
                  supplierOptions={supplierOptions}
                  costPrices={costPrices}
                  form={editForm} setForm={setEditForm}
                  saving={editSaving} saveDisabled={editForm.price == null}
                  onSave={() => onEditSave(q.id, part.id)}
                  onCancel={() => { setEditingQuoteId(null); setEditForm(EMPTY_FORM); }}
                  bg="#fffbe6"
                  isSelectedQuote={part.selected_quote_id === q.id}
                />
              ) : (
                <QuoteRow
                  key={q.id}
                  quote={q}
                  pricing={quotePricing[q.id] ?? null}
                  isSelected={part.selected_quote_id === q.id}
                  isSelecting={isSelecting}
                  onSelect={() => onSelectQuote(part.id, q.id)}
                  onEdit={() => {
                    setEditingQuoteId(q.id);
                    const cp = Number(q.quoted_price);
                    const existing = quotePricing[q.id];
                    const rec = existing ?? recommendedPricing(costPrices, cp);
                    setEditForm({
                      supplier_id: q.supplier_id,
                      price: cp,
                      warranty_days: q.warranty_days,
                      delivery_charge: Number(q.delivery_cost),
                      gp: rec.gp,
                      sp: rec.sp,
                    });
                  }}
                  onDelete={() => onDeleteQuote(q.id, part.id)}
                />
              )
            )}
            {isAdding && (
              <QuoteFormRow
                supplierOptions={supplierOptions}
                costPrices={costPrices}
                form={addForm} setForm={setAddForm}
                saving={addSaving} saveDisabled={!addForm.supplier_id || addForm.price == null}
                onSave={() => onAddQuote(part.id)}
                onCancel={() => { setAddingPartId(null); setAddForm(EMPTY_FORM); }}
                bg="#f0f9ff"
                isSelectedQuote={false}
              />
            )}
          </tbody>
        </table>
      )}

      {/* Add quote button */}
      {!isAdding && (
        <div style={{ padding: "10px 14px", borderTop: quotes.length > 0 ? `1px solid ${t.border}` : "none" }}>
          <Button size="small" type="dashed" icon={<PlusOutlined />} block
            onClick={() => { setAddingPartId(part.id); setAddForm(EMPTY_FORM); }}
          >
            Add Quote
          </Button>
        </div>
      )}

      {/* Supplier price history — always available, but only takes up
          space by default when there's nothing else here yet (see
          docs/superpowers/specs/2026-08-22-supplier-price-history-design.md).
          Matches under a week old get a one-click "use this price" that
          pre-fills the Add Quote form above; older ones are reference only,
          since the price/availability may have moved on by now. */}
      {history.length > 0 && (
        <Collapse
          ghost
          size="small"
          defaultActiveKey={quotes.length === 0 ? ["history"] : []}
          style={{ borderTop: `1px solid ${t.border}` }}
          items={[{
            key: "history",
            label: (
              <span style={{ fontSize: 12, color: t.textSub }}>
                {history.length} past quote{history.length !== 1 ? "s" : ""} for this part
              </span>
            ),
            children: (
              <PartHistoryList
                matches={history}
                renderAction={match => (
                  match.quoted_at && dayjs().diff(dayjs(match.quoted_at), "day") < 7 ? (
                    <Button size="small" type="link" onClick={() => onUseHistoryPrice?.(match)}>
                      Use this price
                    </Button>
                  ) : null
                )}
              />
            ),
          }]}
        />
      )}
    </div>
  );
}

// ── Quote row (tr) ────────────────────────────────────────────────────────────

function QuoteRow({ quote, pricing, isSelected, isSelecting, onSelect, onEdit, onDelete }) {
  const supplierLabel = quote.supplier_name && quote.supplier_name !== quote.supplier_number
    ? quote.supplier_name : quote.supplier_number;
  const showNumber = quote.supplier_name && quote.supplier_name !== quote.supplier_number;
  const cp = Number(quote.quoted_price || 0);
  const { t, isDark } = useQv4Theme();

  return (
    <tr style={{ background: isSelected ? (isDark ? "rgba(69,153,34,0.12)" : "#f6ffed") : undefined, transition: "background 0.2s" }}>
      <td style={{ ...TD_STYLE, paddingLeft: 14 }}>
        <Tooltip title={isSelected ? "Deselect" : "Select this quote"}>
          <button
            aria-label={`${isSelected ? "Deselect" : "Select"} quote from ${supplierLabel}`}
            onClick={onSelect}
            disabled={isSelecting}
            style={{
              width: 20, height: 20, borderRadius: "50%",
              border: "none", background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isSelecting ? "default" : "pointer",
              padding: 0, outline: "none", opacity: isSelecting ? 0.5 : 1,
            }}
          >
            {isSelected
              ? <CheckCircleFilled style={{ fontSize: 18, color: "#52c41a" }} />
              : <span style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${t.border}`, display: "block" }} />
            }
          </button>
        </Tooltip>
      </td>
      <td style={TD_STYLE}>
        <div style={{ fontWeight: 500, fontSize: 12 }}>{supplierLabel}</div>
        {showNumber && <div style={{ fontSize: 10, color: t.textSub }}>{quote.supplier_number}</div>}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>{cp.toLocaleString()}</td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: t.textSub, fontSize: 12 }}>
        {quote.warranty_days ? `${quote.warranty_days}d` : "—"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: t.textSub, fontSize: 12 }}>
        {Number(quote.delivery_cost) > 0 ? Number(quote.delivery_cost).toLocaleString() : "Free"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: t.textSub, fontSize: 12 }}>
        {pricing?.gp != null ? `${(Math.round(pricing.gp * 100) / 100).toLocaleString()}%` : "—"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>
        {pricing?.sp != null ? `AED ${(Math.round(pricing.sp * 100) / 100).toLocaleString()}` : "—"}
      </td>
      <td style={{ ...TD_STYLE, paddingRight: 14 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip title="Edit">
            <Button aria-label={`Edit quote from ${supplierLabel}`} size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this quote?" okText="Yes" cancelText="No" onConfirm={onDelete}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

// ── Quote form row (tr) — used for both add and edit ──────────────────────────

function QuoteFormRow({ supplierOptions, costPrices, form, setForm, onSave, onCancel, saving, saveDisabled, bg, isSelectedQuote }) {
  const priced = form.price != null;
  const { isDark } = useQv4Theme();
  const rowBg = isDark
    ? (bg === "#fffbe6" ? "rgba(250,173,20,0.12)" : bg === "#f0f9ff" ? "rgba(24,144,255,0.12)" : bg)
    : bg;

  // Cost drives a full recompute every keystroke — always overrides whatever
  // GP%/SP held before, since cost is the one field allowed to touch both.
  const onCostChange = (v) => {
    const rec = recommendedPricing(costPrices, v);
    setForm(f => ({ ...f, price: v, gp: rec.gp, sp: rec.sp }));
  };
  // GP%/SP only affect each other — cost is never touched by either.
  const onGpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, gp: v, sp: v != null ? spFromGp(f.price, v) : f.sp }));
  };
  const onSpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, sp: v, gp: v != null ? gpFromSp(f.price, v) : f.gp }));
  };
  const onResetPricing = () => {
    if (!priced) return;
    const rec = recommendedPricing(costPrices, form.price);
    setForm(f => ({ ...f, gp: rec.gp, sp: rec.sp }));
  };

  const supplierSelect = (
    <Select
      showSearch placeholder="Supplier"
      value={form.supplier_id}
      options={supplierOptions}
      filterOption={(input, option) => {
        const q = input.toLowerCase();
        return (option.name || "").toLowerCase().includes(q) || (option.number || "").toLowerCase().includes(q);
      }}
      optionRender={opt => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>
            {opt.data.name && opt.data.name !== opt.data.number ? opt.data.name : opt.data.number}
          </div>
          {opt.data.name && opt.data.name !== opt.data.number && (
            <div style={{ fontSize: 11, color: "#aaa" }}>{opt.data.number}</div>
          )}
        </div>
      )}
      onChange={v => setForm(f => ({ ...f, supplier_id: v }))}
      size="small" style={{ width: "100%" }}
    />
  );

  return (
      <>
        {!isSelectedQuote && (
          <tr style={{ background: rowBg }}>
            <td colSpan={8} style={{ padding: "6px 14px 2px" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 11, color: "#874d00", background: isDark ? "rgba(250,173,20,0.12)" : "#fffbe6",
                border: "1px solid #ffe58f", borderRadius: 4, padding: "4px 8px",
              }}>
                <InfoCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
                Preview prices — GP% and Sell Price here aren't saved until this quote is selected as the customer's chosen quote.
              </div>
            </td>
          </tr>
        )}
        <tr data-testid="quote-form-supplier-row" style={{ background: rowBg }}>
          <td colSpan={8} style={{ padding: "8px 14px 4px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: "#8c8c8c" }}>SUPPLIER</div>
            {supplierSelect}
          </td>
        </tr>
        <tr data-testid="quote-form-fields-row" style={{ background: rowBg }}>
          <td colSpan={8} style={{ padding: "4px 14px 10px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) minmax(95px, .8fr) minmax(110px, .9fr) minmax(120px, 1fr) minmax(140px, 1.1fr) auto", gap: 8, alignItems: "end" }}>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>COST</div>
                <InputNumber placeholder="Price" value={form.price} onChange={onCostChange} min={0} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>WARRANTY</div>
                <InputNumber placeholder="Days" value={form.warranty_days} onChange={v => setForm(f => ({ ...f, warranty_days: v }))} min={0} size="small" style={{ width: "100%" }} controls={false} />
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>DELIVERY</div>
                <InputNumber placeholder="Charge" value={form.delivery_charge} onChange={v => setForm(f => ({ ...f, delivery_charge: v }))} min={0} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>GP %</div>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <InputNumber placeholder="GP%" value={form.gp != null ? Math.round(form.gp * 100) / 100 : undefined} onChange={onGpChange} disabled={!priced} min={0} max={99} precision={2} size="small" style={{ width: "100%" }} suffix="%" controls={false} />
                  {priced && <Tooltip title="Reset to recommended GP%/price"><Button size="small" type="text" icon={<UndoOutlined />} onClick={onResetPricing} style={{ flexShrink: 0, padding: "0 2px" }} /></Tooltip>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>SELL PRICE</div>
                <InputNumber placeholder="Sell price" value={form.sp != null ? Math.round(form.sp * 100) / 100 : undefined} onChange={onSpChange} disabled={!priced} min={0} precision={2} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <Button aria-label="Cancel quote changes" size="small" icon={<CloseOutlined />} onClick={onCancel} />
                <Button aria-label="Save quote" size="small" type="primary" icon={<CheckOutlined />} loading={saving} disabled={saveDisabled} onClick={onSave} />
              </div>
            </div>
          </td>
        </tr>
      </>
  );
}

// ── Share Quote Modal ─────────────────────────────────────────────────────────

export function injectPartsTemplate(templateText, card, selectedParts, deliveryPerPart) {
  const customerName = card?.customer?.customer_name || card?.customer_name || "";
  const queryId = card?.query_id || card?.id || "";

  // Substitute placeholder if value is non-empty; otherwise drop the entire line
  const replaceOrDrop = (text, placeholder, value) => {
    if (value) return text.replace(new RegExp(`\\{\\{${placeholder}\\}\\}`, "gi"), value);
    return text.replace(new RegExp(`[^\n]*\\{\\{${placeholder}\\}\\}[^\n]*\n?`, "gi"), "");
  };

  let result = templateText.replace(
    /\{\{#parts\}\}([\s\S]*?)\{\{\/parts\}\}/g,
    (_, block) =>
      selectedParts.map(({ part, quote, sp, vatPct, vatMode, oldExchange }) => {
        const basePrice = (sp ?? Number(quote.quoted_price || 0)) + deliveryPerPart;
        const finalPrice = Math.round(vatMode === "incl" ? basePrice * (1 + vatPct / 100) : basePrice);
        const vatText = vatMode === "incl" ? `(Incl. VAT ${vatPct}%)` : vatMode === "excl" ? `+ VAT ${vatPct}%` : "";
        const priceText = finalPrice.toLocaleString();
        const wd = Number(quote.warranty_days || 0);
        const warrantyText = wd > 0 ? `${wd} days replacement warranty` : "";

        // Appended once, at the very end of the price/VAT line — e.g.
        // "AED 450 + VAT 5% (With Old Exchange)". Guarded so it's added
        // exactly once even if a template uses {{price}} and {{vat}}
        // unpaired (each substituted by a separate replace below).
        let oldExchangeAppended = false;
        const withOldExchangeSuffix = (text) => {
          if (!oldExchange || oldExchangeAppended) return text;
          oldExchangeAppended = true;
          return `${text} (With Old Exchange)`;
        };

        let b = block;
        b = replaceOrDrop(b, "part_name",      part.part_name || "");
        b = replaceOrDrop(b, "side",            sideLabel(part.side || ""));
        b = replaceOrDrop(b, "partnumber",      part.part_number || "");
        b = replaceOrDrop(b, "part_condition",  conditionLabel(part.condition_preference ?? part.condition ?? part.part_condition ?? ""));
        b = replaceOrDrop(b, "qty",             part.quantity ? String(part.quantity) : "");
        // Price + VAT on same line — combine to avoid trailing space when VAT is off
        b = b.replace(
          /\{\{price\}\}(\s*)\{\{vat\}\}/gi,
          (_, sep) => withOldExchangeSuffix(vatText ? `${priceText}${sep}${vatText}` : priceText)
        );
        b = b.replace(/\{\{price\}\}/gi, () => withOldExchangeSuffix(priceText));
        b = b.replace(/\{\{vat\}\}/gi,   () => withOldExchangeSuffix(vatText));
        b = replaceOrDrop(b, "warranty", warrantyText);

        return b;
      }).join("")
  );

  result = result
    .replace(/\{\{name\}\}/gi,          customerName)
    .replace(/\{\{customer_name\}\}/gi, customerName)
    .replace(/\{\{query_id\}\}/gi,      String(queryId))
    .replace(/\{\{query_number\}\}/gi,  String(queryId));

  return result;
}

function ShareQuoteModal({ open, onClose, card, taskCustomer, taskCustomerType, parts, quotePricing, orderDelivery, authState, userId, customerPhone, readOnly, preloadedPayload, onApprovalCreated, resubmissionReason, onSuggestUnavailableNotice }) {
  const { t, isDark } = useQv4Theme();

  const [quickReplies, setQuickReplies] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("b2c");
  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [partSp, setPartSp] = useState({}); // { [partId]: number } — overrideable SP per part
  // One global VAT mode/% applied to every included part in this send —
  // no per-part VAT choice anymore (was `partVat`, a per-part map).
  const [vatMode, setVatMode] = useState("excl");
  const [vatPct, setVatPct] = useState(5);
  const [partOldExchange, setPartOldExchange] = useState({}); // { [partId]: boolean }
  const [deliveryAmount, setDeliveryAmount] = useState(0);
  // Floor for deliveryAmount — the amount can be raised but never dropped
  // below what it started at, since delivery is no longer optional.
  const [minDeliveryAmount, setMinDeliveryAmount] = useState(0);
  const [messageText, setMessageText] = useState("");
  const [textEditEnabled, setTextEditEnabled] = useState(false);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  const [sending, setSending] = useState(false);
  const [manualShare, setManualShare] = useState(null);
  const [recordingManual, setRecordingManual] = useState(false);
  // Bumped when the condition code→label map arrives so labels replace raw codes
  const [condVersion, setCondVersion] = useState(0);

  // customer_type is numeric: 0 = B2B, 1 = B2C, null = unknown
  // taskCustomerType is extracted from the full task fetch and is the most reliable source
  const rawType = readOnly
    ? (preloadedPayload?.customer_type ?? null)
    : (taskCustomerType ?? taskCustomer?.type ?? taskCustomer?.customer_type ?? card?.customer_type ?? card?.customer?.type ?? card?.customer?.customer_type ?? null);
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";
  const typeKnown = isB2B || isB2C;

  useEffect(() => {
    if (!open) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    loadSideLabels().then(() => setCondVersion(v => v + 1));
    if (readOnly && preloadedPayload) {
      // Hydrate from the stored payload snapshot
      const sp = {};
      const oldExchange = {};
      const included = new Set();
      const firstPart = (preloadedPayload.parts ?? [])[0];
      // vat_mode is the current field; fall back to the old boolean for
      // payload snapshots saved before the Excl. VAT option existed. Every
      // part in one send shares the same mode/% going forward — the first
      // included part's values are a reasonable single representative for
      // older payloads that might still vary per part.
      setVatMode(firstPart ? (firstPart.vat_mode ?? (firstPart.vat_enabled ? "incl" : "none")) : "excl");
      setVatPct(firstPart?.vat_pct ?? 5);
      (preloadedPayload.parts ?? []).forEach(p => {
        sp[p.query_part_id] = p.sp;
        oldExchange[p.query_part_id] = !!p.old_exchange;
        if (p.included) included.add(p.query_part_id);
      });
      setPartSp(sp);
      setPartOldExchange(oldExchange);
      setIncludedPartIds(included);
      setDeliveryAmount(preloadedPayload.delivery_amount ?? 0);
      setMinDeliveryAmount(preloadedPayload.delivery_amount ?? 0);
      setActiveTab(preloadedPayload.template_type ?? "b2c");
      setMessageText(preloadedPayload.message_text ?? "");
      setTextManuallyEdited(true); // suppress any re-injection
      setTextEditEnabled(false);
      setSending(false);
      return;
    }
    setActiveTab(isB2B ? "b2b" : "b2c");
    setIncludedPartIds(new Set(parts.filter(p => p.selected_quote_id).map(p => p.id)));
    const sp = {};
    const oldExchange = {};
    parts.forEach(p => {
      const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
      sp[p.id] = quotePricing[p.selected_quote_id]?.sp ?? Number(quote?.quoted_price || 0);
      // Forward-compatible: not persisted on query_parts yet (see
      // FUTURE_TASKS.md), so this is always false today, but reading it
      // means nothing else needs to change once the backend adds it.
      oldExchange[p.id] = !!p.old_exchange;
    });
    setPartSp(sp);
    setVatMode("excl");
    setVatPct(5);
    setPartOldExchange(oldExchange);
    setDeliveryAmount(orderDelivery ?? 0);
    setMinDeliveryAmount(orderDelivery ?? 0);
    setTextEditEnabled(false);
    setTextManuallyEdited(false);
    setSending(false);
    if (quickReplies.length) return;
    setQrLoading(true);
    fetchQuickReplies(userId).then(list => {
      setQuickReplies(list.filter(qr => !qr.type || qr.type === "text"));
      setQrLoading(false);
    });
  }, [open]); // eslint-disable-line

  const selectedPartsForInjection = useMemo(() => {
    if (readOnly && preloadedPayload) {
      return (preloadedPayload.parts ?? [])
        .filter(p => p.included)
        .map(p => ({
          part: { part_name: p.part_name, condition_preference: p.condition },
          quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
          sp: p.sp,
          vatPct: p.vat_pct ?? 5,
          vatMode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none"),
          oldExchange: partOldExchange[p.query_part_id] ?? !!p.old_exchange,
        }));
    }
    return parts
      .filter(p => includedPartIds.has(p.id) && p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const sp = partSp[p.id] ?? quotePricing[p.selected_quote_id]?.sp ?? Number(quote?.quoted_price || 0);
        return { part: p, quote, sp, vatPct, vatMode, oldExchange: !!partOldExchange[p.id] };
      })
      .filter(({ quote }) => !!quote);
  }, [readOnly, preloadedPayload, parts, includedPartIds, partSp, vatPct, vatMode, partOldExchange]); // eslint-disable-line

  const deliveryPerPart = selectedPartsForInjection.length > 0
    ? (deliveryAmount || 0) / selectedPartsForInjection.length
    : 0;

  const getTemplate = (tab) => {
    const id = tab === "b2b" ? QR_IDS.b2b : QR_IDS.b2c;
    return quickReplies.find(qr => qr.id === id || qr._id === id);
  };

  useEffect(() => {
    if (!open || textManuallyEdited) return;
    const qr = getTemplate(activeTab);
    if (!qr) { setMessageText(""); return; }
    const base = substituteQRText(qr.text || "", authState);
    setMessageText(injectPartsTemplate(base, card, selectedPartsForInjection, deliveryPerPart));
  }, [activeTab, selectedPartsForInjection, quickReplies, open, deliveryPerPart, condVersion]); // eslint-disable-line

  const togglePart = (partId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId); else next.add(partId);
      return next;
    });
  };

  const toggleOldExchange = (partId) =>
    setPartOldExchange(prev => ({ ...prev, [partId]: !prev[partId] }));

  const handleSend = async () => {
    if (selectedPartsForInjection.length === 0) {
      antMessage.warning("Select at least one part to include in the quote before sending.");
      return;
    }
    if (!messageText.trim() || !customerPhone) return;

    const partsPayload = parts
      .filter(p => p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        if (!quote) return null;
        const isIncluded = includedPartIds.has(p.id);
        const sp = partSp[p.id] ?? quotePricing[p.selected_quote_id]?.sp ?? Number(quote.quoted_price || 0);
        const cp = Number(quote.quoted_price || 0);
        const pct = vatPct;
        const mode = vatMode;
        const share = isIncluded ? deliveryPerPart : 0;
        const basePrice = sp + share;
        const finalPrice = Math.round(mode === "incl" ? basePrice * (1 + pct / 100) : basePrice);
        return {
          query_part_id: p.id,
          part_name: p.part_name || "",
          quote_id: p.selected_quote_id,
          included: isIncluded,
          sp,
          cp,
          condition: conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New",
          warranty_days: Number(quote.warranty_days || 0),
          vat_enabled: mode !== "none", // kept for older readers of this payload shape
          vat_mode: mode,
          vat_pct: pct,
          delivery_share: isIncluded ? Number(deliveryPerPart.toFixed(2)) : 0,
          final_price: finalPrice,
          old_exchange: !!partOldExchange[p.id],
        };
      })
      .filter(Boolean);

    const payload = {
      query_pk: card?.id,
      query_id: card?.query_id,
      customer_name: card?.customer?.customer_name || card?.customer_name || "",
      customer_phone: customerPhone,
      customer_type: rawType,
      userId,
      submitted_by: userId,
      submitted_by_name: authState?.user?.name || authState?.user?.username || "",
      template_type: activeTab,
      message_text: messageText.trim(),
      message_customised: textManuallyEdited,
      delivery_enabled: true,
      delivery_amount: deliveryAmount || 0,
      parts: partsPayload,
      ...(resubmissionReason ? { resubmission_reason: resubmissionReason } : {}),
    };

    setSending(true);
    try {
      const preflight = await fetchQuoteSharePreflight(card?.id);
      if (!preflight?.success) throw new Error(preflight?.message || "Could not verify quote sharing availability");
      const decision = preflight.data;
      if (!decision?.approval_required && decision?.communication_channel?.key !== "wati") {
        setManualShare({ payload, channel: decision.communication_channel });
        return;
      }

      const res = await shareQuote(card?.id, payload);
      if (!res.success) throw new Error(res.message);
      if (res.mode === "approval_required") {
        antMessage.info("Quote submitted for admin approval");
        onApprovalCreated?.();
      } else {
        antMessage.success(res.message || "Quote shared with customer");
        // The customer just heard good news about some parts — nudge staff to
        // also tell them about any part nobody ever quoted, in the same beat.
        const zeroQuoteParts = parts.filter(p => (p.supplier_quotes ?? p.quotes ?? []).length === 0);
        if (zeroQuoteParts.length > 0 && onSuggestUnavailableNotice) {
          Modal.confirm({
            title: "Some parts still have no quotes",
            content: `${zeroQuoteParts.length} part(s) — ${zeroQuoteParts.map(p => p.part_name).join(", ")} — have no supplier quotes. Send the customer a message about them now? You can also do this later from the task drawer's "Notify Unavailable Parts" button.`,
            okText: "Send now",
            cancelText: "Not now",
            onOk: () => onSuggestUnavailableNotice(zeroQuoteParts),
          });
        }
      }
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || e?.message || "Failed to submit quote");
    } finally {
      setSending(false);
    }
  };

  const handleManualShare = async () => {
    if (!manualShare?.payload) return;
    setRecordingManual(true);
    try {
      const res = await shareQuote(card?.id, manualShare.payload);
      if (!res.success) throw new Error(res.message);
      if (res.mode === "approval_required") {
        antMessage.info("Quote submitted for admin approval");
        onApprovalCreated?.();
      } else {
        antMessage.success(res.message || "Quote sharing recorded");
      }
      setManualShare(null);
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || e?.message || "Failed to record quote");
    } finally {
      setRecordingManual(false);
    }
  };

  const unquotedParts = readOnly ? [] : parts.filter(p => !p.selected_quote_id);
  const customerName = readOnly
    ? (preloadedPayload?.customer_name || "—")
    : (card?.customer?.customer_name || card?.customer_name || "—");
  const queryId = readOnly ? preloadedPayload?.query_id : (card?.query_id || card?.id);

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {readOnly && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="This is a read-only preview of the quote sent for approval."
        />
      )}
      <div style={{ padding: "8px 10px", background: t.hover, borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: t.textSub, marginBottom: 6 }}><strong style={{ color: t.text }}>Query:</strong> #{queryId}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={32}
            icon={isB2B ? <ShopOutlined /> : isB2C ? <UserOutlined /> : <QuestionCircleOutlined />}
            style={{ backgroundColor: isB2B ? "#1677ff" : isB2C ? "#722ed1" : "#d48806", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: t.text }}>{customerName}</div>
            <div style={{ color: t.textSub, fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
              {!readOnly && customerPhone ? ` · ${customerPhone}` : ""}
            </div>
          </div>
        </div>
      </div>

      {unquotedParts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message={`${unquotedParts.length} part${unquotedParts.length > 1 ? "s have" : " has"} no selected quote — excluded`}
        />
      )}

      {/* Order-level delivery — always charged, not optional (see toggleable
          history below); the amount stays editable. */}
      <div style={{
        border: "1px solid #91caff", borderRadius: 6, padding: "8px 10px",
        flexShrink: 0, background: isDark ? "rgba(24,144,255,0.12)" : "#e6f7ff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 12 }}>Delivery charge</span>
          <span style={{ fontSize: 11, color: t.textSub }}>(Permanent, Operational Cost)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            value={deliveryAmount}
            min={minDeliveryAmount}
            size="small"
            prefix="AED"
            disabled={readOnly}
            style={{ width: 110 }}
            onChange={v => setDeliveryAmount(Math.max(minDeliveryAmount, v ?? 0))}
          />
          {selectedPartsForInjection.length > 1 && (
            <span style={{ fontSize: 11, color: t.textSub }}>
              ÷ {selectedPartsForInjection.length} parts
              {" = "}
              <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
            </span>
          )}
        </div>
      </div>

      {/* One global VAT mode/% applied to every included part in this
          send — replaces the old per-part VAT dropdown/%. */}
      <div style={{
        border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 10px",
        flexShrink: 0, background: t.headerBg,
      }}>
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>VAT (applies to every included part)</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 168, flexShrink: 0 }}>
            <Select
              aria-label="VAT mode"
              value={vatMode}
              disabled={readOnly}
              onChange={v => !readOnly && setVatMode(v)}
              size="small"
              style={{ fontSize: 11 }}
              options={[
                { value: "incl", label: "Including VAT" },
                { value: "excl", label: "Excluding VAT" },
                { value: "none", label: "No VAT" },
              ]}
            />
          </div>
          <InputNumber
            aria-label="VAT %"
            value={vatPct}
            min={0} max={100}
            size="small"
            disabled={readOnly || vatMode === "none"}
            suffix="%"
            style={{ width: 84 }}
            onChange={v => !readOnly && setVatPct(v ?? 0)}
          />
        </div>
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: "#595959", flexShrink: 0 }}>Parts to include</div>

      {(readOnly ? (preloadedPayload?.parts ?? []) : parts).map(p => {
        // Normalise field names — live parts use p.id, payload parts use p.query_part_id
        const partKey = readOnly ? p.query_part_id : p.id;
        const quote = readOnly ? null : (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const hasQuote = readOnly ? true : !!quote;
        const checked = readOnly ? !!p.included : includedPartIds.has(partKey);
        const sp = readOnly ? (p.sp ?? 0) : (quote ? (quotePricing[quote.id]?.sp ?? Number(quote.quoted_price || 0)) : null);
        const cp = readOnly ? (p.cp ?? 0) : Number(quote?.quoted_price || 0);
        const { pct, mode } = readOnly
          ? { pct: p.vat_pct ?? 5, mode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none") }
          : { pct: vatPct, mode: vatMode };
        const warrantyDays = readOnly ? (p.warranty_days ?? 0) : Number(quote?.warranty_days || 0);
        const condition = readOnly
          ? (conditionLabel(p.condition) || "New")
          : (conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New");

        const rawSp = readOnly ? sp : (partSp[partKey] ?? sp);
        const currentSp = rawSp ?? 0;
        const gp = hasQuote ? gpFromSp(cp, currentSp) : 0;
        const gpColor = gp > 0 ? "#389e0d" : "#cf1322";
        const withDelivery = rawSp != null ? rawSp + (checked ? deliveryPerPart : 0) : null;
        const finalPrice = readOnly ? p.final_price : (withDelivery != null ? Math.round(mode === "incl" ? withDelivery * (1 + pct / 100) : withDelivery) : null);
        const vatSuffix = mode === "incl" ? `(Incl. VAT ${pct}%)` : mode === "excl" ? `+ VAT ${pct}%` : "";
        const showFinal = hasQuote && sp != null && (deliveryPerPart > 0 || mode !== "none");
        const inclVatPrice = withDelivery != null ? Math.round(withDelivery * (1 + pct / 100)) : null;

        return (
          <div
            key={partKey}
            style={{
              border: `1px solid ${checked && hasQuote ? "#b7eb8f" : hasQuote ? "#d9d9d9" : "#ffbb96"}`,
              borderRadius: 6,
              padding: "8px 10px",
              opacity: hasQuote ? 1 : 0.55,
              background: checked && hasQuote ? "#f6ffed" : undefined,
              flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: hasQuote && !readOnly ? "pointer" : "default" }}
                  onClick={() => hasQuote && !readOnly && togglePart(partKey)}
                >
                  <Checkbox checked={checked} disabled={!hasQuote || readOnly} onChange={() => {}} />
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{p.part_name}</span>
                  {!hasQuote && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>No quote</Tag>}
                  {hasQuote && (
                    <Tooltip title={warrantyDays > 0 ? `${warrantyDays} days replacement warranty` : "No warranty"}>
                      <span style={{ color: "#8c8c8c", fontSize: 11, cursor: "default" }}>
                        {condition}
                        {warrantyDays > 0 ? ` · ${warrantyDays}d` : " · No warranty"}
                      </span>
                    </Tooltip>
                  )}
                  {hasQuote && (
                    <span onClick={e => e.stopPropagation()}>
                      <Checkbox
                        aria-label={`Use old exchange for ${p.part_name}`}
                        checked={readOnly ? !!p.old_exchange : !!partOldExchange[partKey]}
                        disabled={readOnly}
                        onChange={() => !readOnly && toggleOldExchange(partKey)}
                      >
                        <span style={{ fontSize: 11 }}>With Old Exchange</span>
                      </Checkbox>
                    </span>
                  )}
                </div>
                {hasQuote && (
                  <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: "#595959", lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                      <Tooltip title="Cost price — what the supplier quoted us">
                        <span style={{ cursor: "default" }}>
                          CP: <strong style={{ color: t.text }}>AED {cp.toLocaleString()}</strong>
                        </span>
                      </Tooltip>
                      {" · "}
                      <Tooltip title="Gross profit margin based on current selling price">
                        <span style={{ cursor: "default" }}>
                          GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                        </span>
                      </Tooltip>
                      <InputNumber
                        value={rawSp}
                        min={0}
                        size="small"
                        prefix="AED"
                        disabled={readOnly}
                        style={{ width: 110, fontWeight: 600 }}
                        onChange={v => !readOnly && setPartSp(prev => ({ ...prev, [partKey]: v ?? 0 }))}
                      />
                      {checked && deliveryPerPart > 0 && (
                        <Tooltip title={`Delivery share added to this part (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                          <span style={{ color: "#1677ff", cursor: "default" }}>
                            +{deliveryPerPart % 1 === 0 ? deliveryPerPart : deliveryPerPart.toFixed(2)} del
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Spans all 3 rows to the left (name row + both detail rows),
                  vertically centered — this is the number that matters most
                  in this block. */}
              {showFinal && (
                <>
                  <div style={{ width: 1, alignSelf: "stretch", background: t.border, flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0, padding: "0 2px" }}>
                    <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                      <span style={{
                        color: "#389e0d", fontWeight: 700, fontSize: 20,
                        background: "#f6ffed", border: "1px solid #b7eb8f",
                        borderRadius: 6, padding: "4px 12px", cursor: "default",
                        whiteSpace: "nowrap",
                      }}>AED {finalPrice?.toLocaleString()}</span>
                    </Tooltip>
                    {vatSuffix && <span style={{ fontSize: 11, color: "#8c8c8c" }}>{vatSuffix}</span>}
                    {mode !== "none" && inclVatPrice != null && (
                      <span style={{ fontSize: 10, color: "#8c8c8c" }}>Incl. VAT: AED {inclVatPrice.toLocaleString()}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {!typeKnown && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="Customer type not set — select the appropriate template"
        />
      )}
      {!readOnly && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}
      {!readOnly && qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : !readOnly && !getTemplate(activeTab) ? (
        <Alert type="warning" showIcon message="Quick reply template not found for this customer type" />
      ) : (
        <>
          {!readOnly && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <Checkbox
                checked={textEditEnabled}
                onChange={e => {
                  setTextEditEnabled(e.target.checked);
                }}
              >
                <span style={{ fontSize: 12 }}>Edit message directly</span>
              </Checkbox>
              {textManuallyEdited && (
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  Customised — sent as-is, no re-injection
                </Tag>
              )}
              {textManuallyEdited && textEditEnabled && (
                <Tooltip title="Discard your edits and go back to auto-generated text">
                  <Button
                    size="small"
                    type="text"
                    style={{ fontSize: 11, color: "#8c8c8c", padding: "0 4px" }}
                    onClick={() => {
                      setTextManuallyEdited(false);
                      setTextEditEnabled(false);
                      const qr = getTemplate(activeTab);
                      if (qr) {
                        const base = substituteQRText(qr.text || "", authState);
                        setMessageText(injectPartsTemplate(base, card, selectedPartsForInjection, deliveryPerPart));
                      }
                    }}
                  >
                    Reset to template
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
          <Input.TextArea
            value={messageText}
            readOnly={readOnly || !textEditEnabled}
            onChange={e => {
              if (readOnly) return;
              setMessageText(e.target.value);
              setTextManuallyEdited(true);
            }}
            style={{
              fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
              background: !readOnly && textEditEnabled ? t.input : t.headerBg,
              cursor: !readOnly && textEditEnabled ? "text" : "default",
              color: !readOnly && textEditEnabled ? t.text : t.textSub,
            }}
            placeholder="Message preview…"
          />
        </>
      )}
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onCancel={onClose}
      title={readOnly
        ? <span><EyeOutlined style={{ marginRight: 6 }} />Quote preview — pending approval</span>
        : <span><ShareAltOutlined style={{ marginRight: 6 }} />Share quote to customer</span>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        readOnly ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              aria-label="Send quote to customer"
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!messageText.trim()}
              onClick={handleSend}
            >
              Send to customer
            </Button>
          </div>
        )
      }
      width={940}
      destroyOnHidden
      zIndex={1050}
    >
      <div style={{ display: "grid", gridTemplateColumns: "520px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {leftCol}
        {rightCol}
      </div>
    </Modal>
    <Modal
      open={!!manualShare}
      title="Confirm manual quote sharing"
      okText="Record quote as shared"
      cancelText="Cancel"
      confirmLoading={recordingManual}
      onOk={handleManualShare}
      onCancel={() => setManualShare(null)}
      destroyOnHidden
      zIndex={1060}
    >
      <Alert
        type="warning"
        showIcon
        message={`Customer communication: ${manualShare?.channel?.label || "Manual channel"}`}
        description={`This customer/query uses ${manualShare?.channel?.label || "a manual channel"} as the preferred communication channel. Continuing will record that the quote was shared manually; SRM will not send an automated WATI message.`}
      />
    </Modal>
    </>
  );
}
